// src/App.js
import { useEffect, useState } from "react";
import { Routes, Route } from "react-router-dom";

import HomePage from "./pages/HomePage";
import AWurfPage from "./pages/AWurfPage";

import AdminLoginPage from "./admin/AdminLoginPage";
import AdminDashboardPage from "./admin/AdminDashboardPage";
import AdminNewsPage from "./admin/AdminNewsPage";
import AdminNewsFormPage from "./admin/AdminNewsFormPage";
import AdminNoticesPage from "./admin/AdminNoticesPage";
import AdminNoticeFormPage from "./admin/AdminNoticeFormPage";
import AdminDogsPage from "./admin/AdminDogsPage";
import AdminDogFormPage from "./admin/AdminDogFormPage";
import ProtectedAdminRoute from "./admin/ProtectedAdminRoute";

import AdminLitterFormPage from "./admin/AdminLitterFormPage";
import AdminLittersPage from "./admin/AdminLittersPage";
import AdminPuppyFormPage from "./admin/AdminPuppyFormPage";
import AdminPuppiesPage from "./admin/AdminPuppiesPage";

import { supabase } from "./lib/supabaseClient";

import {
    getSliderImages,
    elternTiere as fallbackElternTiere,
    seminare,
    welpenWurf as fallbackWelpenWurf,
    dogsSectionData,
} from "./data/kennelData";

function normalizeAssetUrl(url) {
    if (!url) return url;

    const value = url.toString().trim();

    if (
        value.startsWith("http://") ||
        value.startsWith("https://") ||
        value.startsWith("blob:") ||
        value.startsWith("data:")
    ) {
        return value;
    }

    return value.startsWith("/") ? value : `/${value}`;
}

function normalizeDogList(list = []) {
    return list.map((dog) => ({
        ...dog,
        image: dog.image ? normalizeAssetUrl(dog.image) : dog.image,
        imageSrc: dog.imageSrc ? normalizeAssetUrl(dog.imageSrc) : dog.imageSrc,
        image_src: dog.image_src ? normalizeAssetUrl(dog.image_src) : dog.image_src,
        image_url: dog.image_url ? normalizeAssetUrl(dog.image_url) : dog.image_url,
    }));
}

function getDetailValue(details = [], label) {
    if (!Array.isArray(details)) return "";

    const found = details.find(
        (item) =>
            item?.label?.toString().toLowerCase() ===
            label.toString().toLowerCase()
    );

    return found?.value || "";
}

function mapDogForSection(dog) {
    const imageSrc = normalizeAssetUrl(
        dog.image_src || dog.imageSrc || dog.image || dog.image_url || ""
    );

    return {
        id: dog.id,
        name: dog.name,
        sex: dog.sex,
        imageSrc,
        image: imageSrc,
        image_src: imageSrc,
        subtitle: dog.subtitle || "",
        description: dog.description || "",
        badges: dog.badges || [],
        details: dog.details || [],
        highlights: dog.highlights || [],
    };
}

function mapDogForParentCard(dog, role) {
    if (!dog) return null;

    const details = dog.details || [];
    const image = normalizeAssetUrl(
        dog.image_src || dog.imageSrc || dog.image || dog.image_url || ""
    );

    return {
        id: dog.id,
        name: dog.name,
        role,
        breed: getDetailValue(details, "Rasse") || "Havaneser",
        color: getDetailValue(details, "Farbe") || "",
        age: dog.subtitle || "",
        description: dog.description || "",
        character: getDetailValue(details, "Charakter") || "",
        awards: dog.badges || [],
        health: getDetailValue(details, "Gesundheit") || "",
        image,
        imageSrc: image,
        image_src: image,
    };
}

function formatDateForDisplay(value) {
    if (!value) return "";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
}

function mapLitterForSection(litter) {
    const mother = mapDogForParentCard(litter.mother, "Mutter");
    const father = mapDogForParentCard(litter.father, "Vater");

    const parents = [mother?.name, father?.name].filter(Boolean).join(" & ");

    const puppies = (litter.puppies || [])
        .slice()
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
        .map((puppy) => {
            const image = normalizeAssetUrl(
                puppy.image || puppy.image_src || puppy.image_url || ""
            );

            return {
                id: puppy.id,
                name: puppy.name,
                gender: puppy.gender || "",
                color: puppy.color || "",
                character: puppy.character || "",
                status: puppy.status || "",
                image,
                imageSrc: image,
                image_src: image,
                sortOrder: puppy.sort_order ?? 0,
            };
        });

    const groupImage = normalizeAssetUrl(
        litter.group_image || litter.groupImage || litter.image_url || ""
    );

    return {
        id: litter.id,
        slug: litter.slug,
        name: litter.name,
        date: formatDateForDisplay(litter.birth_date),
        birthDate: litter.birth_date,
        count: litter.count_label || "",
        countLabel: litter.count_label || "",
        parents,
        description: litter.description || "",
        groupImage,
        group_image: groupImage,
        mother,
        father,
        parentsDogs: [mother, father].filter(Boolean),
        puppies,
        sortOrder: litter.sort_order ?? 0,
    };
}

function normalizeFallbackWurf(wurf) {
    if (!wurf) return null;

    const groupImage = normalizeAssetUrl(wurf.groupImage || wurf.group_image || "");

    return {
        ...wurf,
        groupImage,
        group_image: groupImage,
        puppies: (wurf.puppies ?? []).map((puppy) => {
            const image = normalizeAssetUrl(
                puppy.image || puppy.imageSrc || puppy.image_src || ""
            );

            return {
                ...puppy,
                image,
                imageSrc: image,
                image_src: image,
            };
        }),
    };
}

export default function HundezuchtWebsite({
                                              kennelName = "Havaneser vom Schnüsterhus",
                                              primaryColor = "#8D6E63",
                                              secondaryColor = "#A1887F",
                                              accentColor = "#BCAAA4",
                                              sliderInterval = 5000,
                                          }) {
    const [dogs, setDogs] = useState([]);
    const [hasLoadedDogs, setHasLoadedDogs] = useState(false);

    const [litters, setLitters] = useState([]);
    const [hasLoadedLitters, setHasLoadedLitters] = useState(false);

    useEffect(() => {
        async function loadDogs() {
            const { data, error } = await supabase
                .from("dogs")
                .select("*")
                .eq("is_visible", true)
                .order("sort_order", { ascending: true });

            if (error) {
                console.error("Fehler beim Laden der Hunde:", error);
                setHasLoadedDogs(true);
                return;
            }

            setDogs(data || []);
            setHasLoadedDogs(true);
        }

        loadDogs();
    }, []);

    useEffect(() => {
        async function loadLitters() {
            const { data, error } = await supabase
                .from("litters")
                .select(
                    `
                    *,
                    puppies (*),
                    mother:mother_id (*),
                    father:father_id (*)
                `
                )
                .eq("is_visible", true)
                .order("sort_order", { ascending: true });

            if (error) {
                console.error("Fehler beim Laden der Würfe:", error);
                setHasLoadedLitters(true);
                return;
            }

            setLitters(data || []);
            setHasLoadedLitters(true);
        }

        loadLitters();
    }, []);

    const sliderImages = getSliderImages().map((img) => ({
        ...img,
        url: normalizeAssetUrl(img.url),
    }));

    const supabaseHuendinnen = dogs
        .filter((dog) => dog.sex === "Hündin" && dog.show_in_dogs_section !== false)
        .map(mapDogForSection);

    const supabaseRueden = dogs
        .filter((dog) => dog.sex === "Rüde" && dog.show_in_dogs_section !== false)
        .map(mapDogForSection);

    const fallbackHuendinnen = normalizeDogList(dogsSectionData?.huendinnen);
    const fallbackRueden = normalizeDogList(dogsSectionData?.rueden);

    const huendinnen =
        hasLoadedDogs && supabaseHuendinnen.length > 0
            ? supabaseHuendinnen
            : fallbackHuendinnen;

    const rueden =
        hasLoadedDogs && supabaseRueden.length > 0
            ? supabaseRueden
            : fallbackRueden;

    const supabaseWelpenWuerfe = litters.map(mapLitterForSection);
    const fallbackWurf = normalizeFallbackWurf(fallbackWelpenWurf);

    const welpenWuerfe =
        hasLoadedLitters && supabaseWelpenWuerfe.length > 0
            ? supabaseWelpenWuerfe
            : fallbackWurf
                ? [fallbackWurf]
                : [];

    const welpenWurf = welpenWuerfe[0] || null;

    const supabaseElternTiere = welpenWuerfe
        .flatMap((wurf) => wurf.parentsDogs || [])
        .filter(Boolean)
        .filter(
            (tier, index, array) =>
                array.findIndex((item) => item.id === tier.id) === index
        );

    const elternTiere =
        hasLoadedLitters && supabaseElternTiere.length > 0
            ? supabaseElternTiere
            : (fallbackElternTiere ?? []).map((tier) => {
                const image = normalizeAssetUrl(
                    tier.image || tier.imageSrc || tier.image_src || ""
                );

                return {
                    ...tier,
                    image,
                    imageSrc: image,
                    image_src: image,
                };
            });

    const sharedProps = {
        kennelName,
        primaryColor,
        secondaryColor,
        accentColor,
        sliderInterval,

        sliderImages,

        elternTiere,

        seminare,

        welpenWurf,
        welpenWuerfe,

        huendinnen,
        rueden,
    };

    return (
        <Routes>
            <Route path="/" element={<HomePage {...sharedProps} />} />

            <Route path="/a-wurf" element={<AWurfPage {...sharedProps} />} />

            <Route
                path="/admin/login"
                element={<AdminLoginPage {...sharedProps} />}
            />

            <Route
                path="/admin"
                element={
                    <ProtectedAdminRoute>
                        <AdminDashboardPage {...sharedProps} />
                    </ProtectedAdminRoute>
                }
            />

            <Route
                path="/admin/news"
                element={
                    <ProtectedAdminRoute>
                        <AdminNewsPage {...sharedProps} />
                    </ProtectedAdminRoute>
                }
            />

            <Route
                path="/admin/news/new"
                element={
                    <ProtectedAdminRoute>
                        <AdminNewsFormPage {...sharedProps} />
                    </ProtectedAdminRoute>
                }
            />

            <Route
                path="/admin/news/:id/edit"
                element={
                    <ProtectedAdminRoute>
                        <AdminNewsFormPage {...sharedProps} />
                    </ProtectedAdminRoute>
                }
            />

            <Route
                path="/admin/notices"
                element={
                    <ProtectedAdminRoute>
                        <AdminNoticesPage {...sharedProps} />
                    </ProtectedAdminRoute>
                }
            />

            <Route
                path="/admin/notices/new"
                element={
                    <ProtectedAdminRoute>
                        <AdminNoticeFormPage {...sharedProps} />
                    </ProtectedAdminRoute>
                }
            />

            <Route
                path="/admin/notices/:id/edit"
                element={
                    <ProtectedAdminRoute>
                        <AdminNoticeFormPage {...sharedProps} />
                    </ProtectedAdminRoute>
                }
            />

            <Route
                path="/admin/dogs"
                element={
                    <ProtectedAdminRoute>
                        <AdminDogsPage {...sharedProps} />
                    </ProtectedAdminRoute>
                }
            />

            <Route
                path="/admin/dogs/new"
                element={
                    <ProtectedAdminRoute>
                        <AdminDogFormPage {...sharedProps} />
                    </ProtectedAdminRoute>
                }
            />

            <Route
                path="/admin/dogs/:id/edit"
                element={
                    <ProtectedAdminRoute>
                        <AdminDogFormPage {...sharedProps} />
                    </ProtectedAdminRoute>
                }
            />

            <Route
                path="/admin/litters"
                element={
                    <ProtectedAdminRoute>
                        <AdminLittersPage {...sharedProps} />
                    </ProtectedAdminRoute>
                }
            />

            <Route
                path="/admin/litters/new"
                element={
                    <ProtectedAdminRoute>
                        <AdminLitterFormPage {...sharedProps} />
                    </ProtectedAdminRoute>
                }
            />

            <Route
                path="/admin/litters/:id/edit"
                element={
                    <ProtectedAdminRoute>
                        <AdminLitterFormPage {...sharedProps} />
                    </ProtectedAdminRoute>
                }
            />

            <Route
                path="/admin/litters/:id/puppies"
                element={
                    <ProtectedAdminRoute>
                        <AdminPuppiesPage {...sharedProps} />
                    </ProtectedAdminRoute>
                }
            />

            <Route
                path="/admin/litters/:id/puppies/new"
                element={
                    <ProtectedAdminRoute>
                        <AdminPuppyFormPage {...sharedProps} />
                    </ProtectedAdminRoute>
                }
            />

            <Route
                path="/admin/litters/:id/puppies/:puppyId/edit"
                element={
                    <ProtectedAdminRoute>
                        <AdminPuppyFormPage {...sharedProps} />
                    </ProtectedAdminRoute>
                }
            />
        </Routes>
    );
}